import { API_URL } from './config'
import { getIdToken } from './auth'

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const token = getIdToken()
  const resp = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: token } : {}),
      ...options?.headers,
    },
  })
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({})) as { error?: string }
    throw new Error(err.error ?? `HTTP ${resp.status}`)
  }
  return resp.json() as Promise<T>
}

export interface InstanceStatus {
  service:        string
  world:          string
  permanent:      boolean
  connectAddress: string
  recentJoins:    number
  onlinePlayers:  string[]
}

export interface StatusResponse {
  instance1: InstanceStatus
  instance2: InstanceStatus
}

export interface GameRules {
  showcoordinates?:      boolean
  keepinventory?:        boolean
  pvp?:                  boolean
  mobgriefing?:          boolean
  dofiretick?:           boolean
  dodaynightcycle?:      boolean
  naturalregeneration?:  boolean
  commandblocksenabled?: boolean
  playerwaypoints?:      boolean
}

export interface World {
  worldId:           string
  name:              string
  permanent?:        boolean
  levelName?:        string
  gamemode?:         string
  lastPlayed?:       string
  clonedFrom?:       string
  filesCopied?:      boolean
  installedPacks?:   { packId: string; name: string }[]
  allowCheats?:      boolean
  gameRules?:        GameRules
  activeOnInstance2?: boolean
}

export interface Player {
  gamertag:           string
  displayName:        string
  xuid?:              string
  role:               'operator' | 'member'
  allowlistInstance1: boolean
  allowlistInstance2: boolean
}

export interface NewPlayer extends Player {
  xuid: string
}

export interface UnknownPlayer {
  gamertag: string
  xuid:     string
  seenOn:   string[]
}

export interface BackupResult {
  backed_up:  boolean
  worldId:    string
  levelName:  string
  backupFile: string
  backupPath: string
  size:       string
  timestamp:  string
}

export interface CloneResult extends World {
  filesCopied:      boolean
  sourceWasMissing: boolean
}

export interface PackComponent {
  uuid:       string
  version:    number[]
  type:       string
  folderName: string
}

export interface Pack {
  packId:      string
  name:        string
  description?: string
  fileName:    string
  s3Key:       string
  uploadedAt:  string
  components:  PackComponent[]
}

export const api = {
  getStatus:   () =>
    apiFetch<StatusResponse>('/status'),

  getWorlds:   () =>
    apiFetch<World[]>('/worlds'),

  switchWorld: (worldId: string) =>
    apiFetch<{ switched: boolean; name: string }>('/worlds/switch', {
      method: 'POST',
      body:   JSON.stringify({ worldId }),
    }),

  cloneWorld: (worldId: string, name?: string) =>
    apiFetch<CloneResult>(`/worlds/${worldId}/clone`, {
      method: 'POST',
      body:   JSON.stringify(name ? { name } : {}),
    }),

  backupWorld: (worldId: string) =>
    apiFetch<BackupResult>(`/worlds/${worldId}/backup`, { method: 'POST' }),

  getPlayers:  () =>
    apiFetch<Player[]>('/players'),

  upsertPlayer: (player: NewPlayer) =>
    apiFetch<{ saved: boolean }>('/players', {
      method: 'POST',
      body:   JSON.stringify(player),
    }),

  lookupGamertag: (gamertag: string) =>
    apiFetch<{ xuid: string; gamertag: string }>(`/players/lookup?gamertag=${encodeURIComponent(gamertag)}`),

  detectNewPlayers: () =>
    apiFetch<{ unknownPlayers: UnknownPlayer[] }>('/players/detect'),

  createWorld: (world: { name: string; gamemode: string; seed?: string; allowCheats?: boolean }) =>
    apiFetch<World>('/worlds', {
      method: 'POST',
      body:   JSON.stringify(world),
    }),

  deleteWorld: (worldId: string, deleteFiles = false) =>
    apiFetch<{ deleted: boolean }>(`/worlds/${worldId}${deleteFiles ? '?deleteFiles=true' : ''}`, { method: 'DELETE' }),

  updateWorldSettings: (worldId: string, settings: { allowCheats?: boolean; gameRules?: GameRules }) =>
    apiFetch<World>(`/worlds/${worldId}`, {
      method: 'PATCH',
      body:   JSON.stringify(settings),
    }),

  getPacks: () =>
    apiFetch<Pack[]>('/packs'),

  getPackUploadUrl: (fileName: string) =>
    apiFetch<{ packId: string; uploadUrl: string; s3Key: string }>('/packs/upload-url', {
      method: 'POST',
      body:   JSON.stringify({ fileName }),
    }),

  registerPack: (pack: { packId: string; fileName: string; name: string; description?: string }) =>
    apiFetch<Pack>('/packs', {
      method: 'POST',
      body:   JSON.stringify(pack),
    }),

  deletePack: (packId: string) =>
    apiFetch<{ deleted: boolean }>(`/packs/${packId}`, { method: 'DELETE' }),

  installPack: (worldId: string, packId: string) =>
    apiFetch<{ installed: boolean }>(`/worlds/${worldId}/packs/${packId}`, { method: 'POST' }),

  removePack: (worldId: string, packId: string) =>
    apiFetch<{ removed: boolean }>(`/worlds/${worldId}/packs/${packId}`, { method: 'DELETE' }),

  getServerVersions: () =>
    apiFetch<{ instance1: string; instance2: string }>('/update/versions'),

  startUpdate: (instance: 1 | 2, url: string) =>
    apiFetch<{ commandId: string; instance: number; started: boolean }>('/update/start', {
      method: 'POST',
      body:   JSON.stringify({ instance, url }),
    }),

  getUpdateStatus: (commandId: string) =>
    apiFetch<{
      status:  string
      steps:   { name: string; state: 'pending' | 'active' | 'done' | 'error' }[]
      version: string | null
      stdout:  string
      stderr:  string
    }>(`/update/status?commandId=${encodeURIComponent(commandId)}`),
}
